import { CHECKLIST_STATE_LABELS, DAMAGE_ZONE_LABELS, FUEL_LEVEL_STEPS, type EntryReportContent } from '@garage/shared';
import type { ReactNode } from 'react';
import { Badge } from '../../../ui/Badge.js';
import { cx } from '../../../ui/cx.js';
import { formatDateTime, formatKm } from '../../lib/format.js';
import { StorageTermsSummary } from '../StorageTermsSummary.js';

/** Niveau de carburant lisible : « Vide », « 3/8 », « 1/2 », « Plein », « Non relevé ». */
export function fuelLabel(level: number | null): string {
  if (level === null) return 'Non relevé';
  if (level === 0) return 'Vide';
  if (level === FUEL_LEVEL_STEPS) return 'Plein';
  const names: Record<number, string> = { 2: '1/4', 4: '1/2', 6: '3/4' };
  return names[level] ?? `${level}/${FUEL_LEVEL_STEPS}`;
}

/** Jauge (lecture seule) : huit segments, les premiers remplis. */
export function FuelGauge({ level }: { level: number | null }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span aria-hidden className="inline-flex gap-0.5">
        {Array.from({ length: FUEL_LEVEL_STEPS }, (_, i) => (
          <span key={i} className={cx('h-3 w-2.5 rounded-sm', level !== null && i < level ? 'bg-ink-2' : 'bg-line-strong')} />
        ))}
      </span>
      <span>{fuelLabel(level)}</span>
    </span>
  );
}

const STATE_TONE = { ok: 'success', remark: 'warning', not_checked: 'neutral' } as const;

/**
 * Contenu d'une fiche de réception, en lecture (aperçu avant validation, fiche validée, portail) :
 * kilométrage, carburant, points de contrôle, dommages par zone, remarques, photos, conditions de
 * gardiennage figées. `photos` rend les photos d'une liste (garage : vignettes ; portail : nombre).
 * Aucun montant : le tarif des conditions n'apparaît que s'il a été renvoyé par l'API.
 */
export function EntryReportSummary({
  content,
  receivedByName,
  photos,
  tz,
}: {
  content: EntryReportContent;
  receivedByName?: string | null;
  photos: (ids: string[]) => ReactNode;
  tz?: string;
}) {
  const remarks = content.checklist.filter((c) => c.state !== 'ok');
  return (
    <div className="space-y-5">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <div>
          <dt className="text-ink-3">Entrée le</dt>
          <dd className="font-medium text-ink tabular-nums">{formatDateTime(content.receivedAt, tz)}</dd>
        </div>
        <div>
          <dt className="text-ink-3">Kilométrage</dt>
          <dd className="font-medium text-ink tabular-nums">{formatKm(content.mileage)}</dd>
        </div>
        <div>
          <dt className="text-ink-3">Carburant ou charge</dt>
          <dd className="font-medium text-ink">
            <FuelGauge level={content.fuelLevel} />
          </dd>
        </div>
        {receivedByName ? (
          <div>
            <dt className="text-ink-3">Réception faite par</dt>
            <dd className="font-medium text-ink">{receivedByName}</dd>
          </div>
        ) : null}
      </dl>

      <div>
        <h4 className="mb-2 text-sm font-semibold text-ink">Points contrôlés</h4>
        <ul className="divide-y divide-line rounded-xl border border-line">
          {content.checklist.map((c) => (
            <li key={c.key} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 px-3 py-2.5">
              <span className="min-w-0 text-ink">
                {c.label}
                {c.comment ? <span className="block text-sm text-ink-2">{c.comment}</span> : null}
              </span>
              <Badge tone={STATE_TONE[c.state]}>{CHECKLIST_STATE_LABELS[c.state]}</Badge>
            </li>
          ))}
        </ul>
        {remarks.length === 0 ? <p className="mt-2 text-sm text-ink-3">Aucune remarque sur les points contrôlés.</p> : null}
      </div>

      <div>
        <h4 className="mb-2 text-sm font-semibold text-ink">Dommages constatés</h4>
        {content.damages.length === 0 ? (
          <p className="text-sm text-ink-3">Aucun dommage relevé.</p>
        ) : (
          <ul className="space-y-2">
            {content.damages.map((d, i) => (
              <li key={`${d.zone}-${i}`} className="rounded-xl border border-line p-3">
                <p className="text-sm font-medium text-ink">{DAMAGE_ZONE_LABELS[d.zone]}</p>
                <p className="text-ink-2">{d.comment}</p>
                {d.mediaIds.length > 0 ? <div className="mt-2">{photos(d.mediaIds)}</div> : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {content.generalNotes ? (
        <div>
          <h4 className="mb-1 text-sm font-semibold text-ink">Remarques</h4>
          <p className="whitespace-pre-line text-ink-2 [unicode-bidi:isolate]">{content.generalNotes}</p>
        </div>
      ) : null}

      {content.mediaIds.length > 0 ? (
        <div>
          <h4 className="mb-2 text-sm font-semibold text-ink">Photos de l’état à l’entrée</h4>
          {photos(content.mediaIds)}
        </div>
      ) : null}

      <div>
        <h4 className="mb-1 text-sm font-semibold text-ink">Conditions de gardiennage remises</h4>
        <StorageTermsSummary terms={content.storageTerms} />
      </div>
    </div>
  );
}
